import { useMemo, useState } from 'react';
import { AdminOverlayPortal } from './AdminOverlayPortal';
import { ORDER_STATUS_LABELS } from './crm/crmLabels';
import { ORDERS_STATUS_HINT } from '../../lib/clientNotifyRules';

const OPS_STATUS_BODY: Record<string, string> = {
  pending: 'Заказ ожидает подтверждения менеджером.',
  confirmed: 'Заказ подтверждён и передан в работу.',
  in_production: 'Заказ в производстве.',
  ready: 'Изделие готово — согласуем выдачу или доставку.',
  delivered: 'Заказ доставлен / выдан.',
  cancelled: 'Заказ отменён.',
};

type Props = {
  orderNumber: string;
  newStatus: string;
  hasTelegram: boolean;
  initialNote?: string;
  onCancel: () => void;
  onConfirm: (opts: { notifyClient: boolean; managerNote: string }) => void;
};

export function OrderStatusNotifySheet({
  orderNumber,
  newStatus,
  hasTelegram,
  initialNote = '',
  onCancel,
  onConfirm,
}: Props) {
  const isCancellation = newStatus === 'cancelled';
  const [notifyClient, setNotifyClient] = useState(hasTelegram && !isCancellation);
  const [note, setNote] = useState(initialNote);

  const statusLabel = ORDER_STATUS_LABELS[newStatus] || newStatus;
  const preview = useMemo(() => {
    const body = OPS_STATUS_BODY[newStatus] ?? 'Статус заказа обновлён.';
    const lines = [
      `📋 Заказ ${orderNumber}`,
      '',
      `Статус: ${statusLabel}`,
      body,
    ];
    const trimmed = note.trim();
    if (trimmed) lines.push('', `💬 Комментарий: ${trimmed}`);
    lines.push('', 'Мини-апп → «Заказы».');
    return lines.join('\n');
  }, [orderNumber, statusLabel, newStatus, note]);

  return (
    <AdminOverlayPortal zIndex={95}>
      <div className="flex-1 flex items-end justify-center min-h-0">
        <div
          className="w-full max-w-lg rounded-t-2xl p-4 space-y-3 max-h-[85dvh] overflow-y-auto"
          style={{ background: '#0F2340', borderTop: '1px solid var(--border-subtle)' }}
        >
          <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            {isCancellation ? 'Отменить заказ?' : `Статус заказа → ${statusLabel}`}
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
            {isCancellation
              ? 'Сделку можно будет найти в фильтре «Отменён». Уведомление в Telegram — только если включите галочку ниже.'
              : `${ORDERS_STATUS_HINT} Клиент видит это во вкладке «Заказы» в мини-апп. Этапы «Замер / Состав» — отдельные сообщения из CRM.`}
          </p>
          {!hasTelegram && (
            <p className="text-[11px] font-bold" style={{ color: '#F6AD55' }}>
              Telegram ID не найден — отправка в бот недоступна.
            </p>
          )}
          <textarea
            className="input-glass w-full text-sm min-h-[64px] rounded-xl p-3"
            placeholder="Комментарий в уведомлении (необязательно)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <label className="flex items-center gap-2 text-xs touch-manipulation min-h-[44px]">
            <input
              type="checkbox"
              checked={notifyClient && hasTelegram}
              disabled={!hasTelegram}
              onChange={(e) => setNotifyClient(e.target.checked)}
              className="w-4 h-4"
            />
            <span style={{ color: 'var(--text-2)' }}>
              {isCancellation ? 'Отправить уведомление об отмене в Telegram' : 'Уведомить клиента в Telegram'}
            </span>
          </label>
          {notifyClient && hasTelegram && (
            <div
              className="rounded-xl p-3 text-[11px] leading-relaxed whitespace-pre-wrap"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
            >
              {preview}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
              style={{ color: 'var(--text-3)' }}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => onConfirm({ notifyClient: notifyClient && hasTelegram, managerNote: note.trim() })}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
              style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
            >
              {isCancellation ? 'Отменить заказ' : 'Сохранить статус'}
            </button>
          </div>
        </div>
      </div>
    </AdminOverlayPortal>
  );
}
